import React, { useState, useEffect } from 'react';
import { FiSave, FiRotateCcw, FiShoppingCart, FiBell } from 'react-icons/fi';
import adminSettingsService from '../../../services/adminSettingsService';
import { Loading } from '../../../components/UI/Loading';

const PurchaseOrderSettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const result = await adminSettingsService.getPurchaseOrderSettings();
      if (result.success) {
        setSettings(result.data);
        setOriginalSettings(result.data);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al cargar configuraciones' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const result = await adminSettingsService.updatePurchaseOrderSettings(settings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Configuración actualizada correctamente' });
        setOriginalSettings(settings);
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al actualizar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('¿Desea descartar los cambios realizados?')) {
      setSettings(originalSettings);
      setMessage(null);
    }
  };

  if (isLoading) {
    return <Loading message="Cargando configuraciones de órdenes de compra..." />;
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  return (
    <div className="settings-page">
      {message && (
        <div className={`alert alert--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Numeración de Órdenes */}
      <div className="settings-section">
        <div className="section-header">
          <FiShoppingCart size={24} />
          <div>
            <h3 className="section-title">Numeración de Órdenes de Compra</h3>
            <p className="section-description">Configuración del formato y secuencia</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Prefijo de Órdenes</label>
            <input
              type="text"
              className="form-input"
              value={settings?.po_prefix || ''}
              onChange={(e) => handleChange('po_prefix', e.target.value)}
              placeholder="PO"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Número Inicial</label>
            <input
              type="number"
              className="form-input"
              value={settings?.po_starting_number || 1000}
              onChange={(e) => handleChange('po_starting_number', parseInt(e.target.value))}
              min="1"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Dígitos de Relleno</label>
            <input
              type="number"
              className="form-input"
              value={settings?.po_number_padding || 6}
              onChange={(e) => handleChange('po_number_padding', parseInt(e.target.value))}
              min="4"
              max="10"
            />
          </div>
        </div>
      </div>

      {/* Términos y Aprobaciones */}
      <div className="settings-section">
        <div className="section-header">
          <FiBell size={24} />
          <div>
            <h3 className="section-title">Términos y Aprobaciones</h3>
            <p className="section-description">Configuración de plazos y flujo de aprobación</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Días de Entrega por Defecto</label>
            <input
              type="number"
              className="form-input"
              value={settings?.default_delivery_days || 15}
              onChange={(e) => handleChange('default_delivery_days', parseInt(e.target.value))}
              min="1"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Términos de Pago (días)</label>
            <input
              type="number"
              className="form-input"
              value={settings?.default_payment_terms || 30}
              onChange={(e) => handleChange('default_payment_terms', parseInt(e.target.value))}
              min="0"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.require_approval || false}
                onChange={(e) => handleChange('require_approval', e.target.checked)}
              />
              <span>Requiere aprobación</span>
            </label>
          </div>

          {settings?.require_approval && (
            <div className="form-group">
              <label className="form-label">Monto Mínimo para Aprobación</label>
              <input
                type="number"
                className="form-input"
                value={settings?.approval_amount_threshold || 0}
                onChange={(e) => handleChange('approval_amount_threshold', parseFloat(e.target.value))}
                min="0"
                step="0.01"
              />
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.allow_partial_receipt || true}
                onChange={(e) => handleChange('allow_partial_receipt', e.target.checked)}
              />
              <span>Permitir recepción parcial</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.auto_create_invoice || false}
                onChange={(e) => handleChange('auto_create_invoice', e.target.checked)}
              />
              <span>Auto-crear factura al recibir</span>
            </label>
          </div>
        </div>
      </div>

      {/* Notificaciones */}
      <div className="settings-section">
        <div className="section-header">
          <FiBell size={24} />
          <div>
            <h3 className="section-title">Notificaciones</h3>
            <p className="section-description">Configurar alertas automáticas</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.notify_on_create || true}
                onChange={(e) => handleChange('notify_on_create', e.target.checked)}
              />
              <span>Notificar al crear orden</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.notify_on_approve || true}
                onChange={(e) => handleChange('notify_on_approve', e.target.checked)}
              />
              <span>Notificar al aprobar orden</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.notify_on_receive || true}
                onChange={(e) => handleChange('notify_on_receive', e.target.checked)}
              />
              <span>Notificar al recibir productos</span>
            </label>
          </div>
        </div>
      </div>

      {/* Botones de Acción */}
      <div className="settings-actions">
        <button
          className="btn btn--outline"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        >
          <FiRotateCcw size={16} />
          Descartar Cambios
        </button>

        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          <FiSave size={16} />
          {isSaving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

export default PurchaseOrderSettings;
